import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from '@/api/generated/schema';
import { authStore } from '@/stores/auth-store';

/** Chemins dont l'appel ne doit JAMAIS déclencher de tentative de rafraîchissement. */
const NO_REFRESH_PATHS = ['/api/v1/auth/login', '/api/v1/auth/refresh', '/api/v1/auth/register'];

/**
 * Erreur métier renvoyée par l'API, au format RFC 9457.
 *
 * `code` est un identifiant STABLE (`auth.invalid_credentials`, `tenant.not_found`...) :
 * c'est lui que le code teste, jamais le message, qui peut être reformulé côté serveur
 * sans préavis. `errors` porte le détail par champ pour les erreurs de validation.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly errors: Record<string, string[]> | undefined;

  constructor(status: number, code: string, message: string, errors?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.errors = errors;
  }

  /** Vrai si l'erreur porte un détail de validation exploitable par un formulaire. */
  get hasFieldErrors(): boolean {
    return this.errors !== undefined && Object.keys(this.errors).length > 0;
  }
}

interface ProblemDetails {
  title?: string;
  detail?: string;
  code?: string;
  errors?: Record<string, string[]>;
}

/**
 * Rafraîchissement de session à VOL UNIQUE (« single flight »).
 *
 * Même raisonnement que côté `frontend/` : la rotation de refresh token révoque le jeton
 * précédent, donc plusieurs rafraîchissements concurrents seraient interprétés côté serveur
 * comme une réutilisation de jeton (un vol) et fermeraient toutes les sessions.
 */
let refreshInFlight: Promise<boolean> | null = null;

/** Même base que le client typé, résolue paresseusement pour rester testable. */
function refreshBaseUrl(): string {
  return (
    import.meta.env.VITE_API_BASE_URL ??
    (typeof window === 'undefined' ? 'http://localhost' : window.location.origin)
  );
}

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await globalThis.fetch(`${refreshBaseUrl()}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        authStore.clear();

        return false;
      }

      const tokens = (await response.json()) as { accessToken: string };
      authStore.setSession(tokens.accessToken);

      return true;
    } catch {
      // Panne réseau : on ne vide PAS la session.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

const authMiddleware: Middleware = {
  onRequest({ request }) {
    const token = authStore.getAccessToken();

    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }

    return request;
  },

  async onResponse({ request, response }) {
    if (response.status !== 401) {
      return response;
    }

    const url = new URL(request.url);

    if (NO_REFRESH_PATHS.some((path) => url.pathname.startsWith(path))) {
      return response;
    }

    if (response.headers.get('X-Token-Expired') !== 'true') {
      await clearIfSessionDead(request, response);

      return response;
    }

    if (!(await refreshSession())) {
      return response;
    }

    const retry = request.clone();
    retry.headers.set('Authorization', `Bearer ${authStore.getAccessToken() ?? ''}`);

    const retried = await globalThis.fetch(retry);

    if (retried.status === 401) {
      await clearIfSessionDead(retry, retried);
    }

    return retried;
  },
};

/** Codes d'un 401 qui signifient « la session n'existe plus », et non « action refusée ». */
const DEAD_SESSION_CODES = ['auth.required', 'auth.session_stale'];

/**
 * Vide la session locale quand un 401 prouve qu'elle est morte côté serveur.
 *
 * Même raisonnement que côté `frontend/` : sans cela, un jeton rejeté restait en mémoire et
 * la garde du routeur laissait naviguer indéfiniment. Les 401 MÉTIER
 * (`auth.invalid_credentials`, défi 2FA invalide) ne déconnectent pas ; le 401 du
 * middleware JWT, lui, n'a pas de corps — donc pas de code.
 */
async function clearIfSessionDead(request: Request, response: Response): Promise<void> {
  if (!request.headers.has('Authorization')) {
    return;
  }

  let code: string | undefined;

  try {
    code = ((await response.clone().json()) as ProblemDetails).code;
  } catch {
    // Corps vide ou non JSON : 401 du middleware d'authentification.
  }

  if (code === undefined || DEAD_SESSION_CODES.includes(code)) {
    authStore.clear();
  }
}

const errorMiddleware: Middleware = {
  async onResponse({ response }) {
    if (response.ok || response.status === 204) {
      return response;
    }

    let problem: ProblemDetails = {};

    try {
      problem = (await response.clone().json()) as ProblemDetails;
    } catch {
      // Réponse non JSON : message générique plutôt que de laisser fuir du HTML.
    }

    throw new ApiError(
      response.status,
      problem.code ?? `http.${response.status}`,
      problem.detail ?? problem.title ?? 'Une erreur inattendue est survenue.',
      problem.errors,
    );
  },
};

/**
 * Client HTTP typé, dérivé du MÊME document OpenAPI que `frontend/` : un seul contrat API
 * pour les deux fronts.
 *
 * Base d'URL : l'origine du document par défaut, comme côté `frontend/` — condition du bon
 * fonctionnement du refresh token, cookie `SameSite=Strict` envoyé uniquement en
 * same-origin. Le proxy Vite du back-office (voir `vite.config.ts`, port 5174) fait pointer
 * `/api` vers `http://localhost:8080`, exactement comme celui de `frontend/`.
 */
const baseUrl =
  import.meta.env.VITE_API_BASE_URL ??
  (typeof window === 'undefined' ? 'http://localhost' : window.location.origin);

export const api = createClient<paths>({
  baseUrl,
  credentials: 'include',

  // PAS de `Content-Type: application/json` ici : fixé au niveau du client, il serait
  // envoyé même sur les GET sans corps (openapi-fetch fusionne les en-têtes globaux
  // APRÈS sa propre règle « pas de Content-Type sans corps »), et FastEndpoints rejette
  // alors la requête en tentant de désérialiser un corps JSON vide (400 Bad Request).
  // openapi-fetch pose déjà ce header lui-même, correctement, sur les requêtes qui ont un
  // corps (POST/PUT avec `body`) — inutile et dangereux de le dupliquer ici.
  fetch: (request) => globalThis.fetch(request),
});

// Même ordre d'enregistrement que côté `frontend/` : `onResponse` s'exécute en ordre
// INVERSE de l'enregistrement, l'authentification doit donc être enregistrée en dernier
// pour intercepter un 401 avant que le middleware d'erreur ne lève une ApiError.
api.use(errorMiddleware);
api.use(authMiddleware);

/** Déclenche la restauration de session au démarrage de l'application. */
export async function bootstrapSession(): Promise<boolean> {
  return refreshSession();
}
